import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  getDinnerBookingByID,
  getDinnerBookingTotalPrice,
  isAllergiesWarningEnabled,
  getDinnerBookingAllergyInformation,
  isDinnerBookingExpanded,
  doesDinnerBookingHaveFSMEligibility,
  isDinnerBookingInBasket,
  hasDinnerBookingBeenModified,
  isDinnerBookingSeen,
  doesDinnerBookingHaveBookings,
  getGoToDinnerBookingID
} from "state/school_money/selectors";
import {
  addDinnerBookingToBasket,
  removeDinnerBookingFromBasket,
  setExpandedDinnerBooking
} from "state/school_money/actions";

import ExpandingBox from "components/ExpandingBox";
import Highlight from "components/Highlight";
import Date from "components/Date";
import ShortDate from "components/ShortDate";
import Currency from "components/Currency";
import AddButton from "components/school_money/AddButton";
import NewIndicator from "components/school_money/NewIndicator";
import WeeklyMealInput from "components/school_money/dinners/WeeklyMealInput";
import AllergiesIndicator from "components/school_money/dinners/AllergiesIndicator";
import AllergiesModal from "components/school_money/dinners/AllergiesModal";
import BasketModificationModal from "components/school_money/BasketModificationModal";

import "./DinnerBooking.css";

class DinnerBooking extends React.Component {

  state = {
    isAllergiesWarningModalOpen: false,
    isBasketModificationModalOpen: false,
  }

  /**
   * This is not called directly from the Add button click
   * confirmAddToBasket is called first, which will show the allergies
   *   warning if applicable
   */
  addToBasket = () => {
    this.props.addToBasket();
    this.props.collapse();
  }

  /**
   * Show the allergies warning alert if applicable
   * If not, just add to basket
   */
  confirmAddToBasket = () => {
    const { isAllergiesWarningEnabled, allergyInformation, isModified, isInBasket } = this.props;
    const hasAllergyInformation = allergyInformation.some( allergyInformation => allergyInformation !== "");
    const isCashless = this.props.dinnerBooking.toJS().isCashless;
    // if the booking is in the basket then show an alert
    if (isInBasket) {
      this.onReadOnlyClick();
    // don't do anything if no changes have been made yet
    } else if (isModified) {
      if (isAllergiesWarningEnabled && hasAllergyInformation && isCashless ) {
        this.setState({ isAllergiesWarningModalOpen: true });
      } else {
        this.addToBasket();
      }
    }
  }

  /**
   * this is only used by the callback of the BasketModificationModal
   */
  removeFromBasket = () => {
    this.props.removeFromBasket();
  }

  /**
   * When the user tries to make a change but the booking is in the basket,
   *   show an alert warning them that they have to remove it first
   */
  onReadOnlyClick = () => {
    this.setState({ isBasketModificationModalOpen: true });
  }

  /**
   * If the modal was closed with a positive/"yes" then remove from basket
   */
   onBasketModificationModalClose = (result) => {
     if (result) {
       this.removeFromBasket();
     }
     this.setState({ isBasketModificationModalOpen: false });
   }

  /**
   * If the modal was closed with a positive/"yes" then add to basket
   * Otherwise just close
   */
  onAllergiesWarningModalClose = (result) => {
    if (result) {
      this.addToBasket();
    }
    this.setState({ isAllergiesWarningModalOpen: false });
  }

  render() {

    const { dinnerBooking, dinnerBookingID, totalPrice, hasFSMEligibility, isExpanded, isInBasket, isModified, isSeen, isBooked, isHighlighted, expand, collapse } = this.props;
    const { description, startDate, isCashless, fsmLimitPerDay, lastModifiedDate } = dinnerBooking.toJS();
    const hasFSMLimit = fsmLimitPerDay !== null;
    const hasLastModifiedDate = lastModifiedDate !== "";

    let status = null;
    if (isBooked) {
      status = "Booked";
    }

    const classNames = [ "dinner-booking" ];
    if (isModified) {
      classNames.push("dinner-booking--is-modified");
    } else {
      classNames.push("dinner-booking--not-modified");
    }
    if (isInBasket) {
      classNames.push("dinner-booking--in-basket");
    }

    return (
      <div className={classNames.join(" ")} ref={this.props.innerRef}>
        <BasketModificationModal
          isOpen={this.state.isBasketModificationModalOpen}
          onClose={this.onBasketModificationModalClose}
          itemName="week"
        />
        <Highlight cssModifier="school_money" isHighlighted={isHighlighted}>
          <ExpandingBox cssModifier="school_money" expanded={isExpanded} onExpand={expand} onCollapse={collapse}>
            <ExpandingBox.Header toggleOnClick={true}>
              { isSeen ? null : <NewIndicator className="dinner-booking__new-indicator" /> }
              <div className="dinner-booking__header">
                <div className="dinner-booking__description">{description}</div>
                <div className="dinner-booking__week-date">W/C <Date date={startDate} /></div>
              </div>
              <div className="dinner-booking__status">{status}</div>
            </ExpandingBox.Header>
            <ExpandingBox.Body toggleOnClick={false}>
              <div className="dinner-booking__body">
                <div className="dinner-booking__meal_selections">
                  <WeeklyMealInput dinnerBookingID={dinnerBookingID} onReadOnlyClick={this.onReadOnlyClick} />
                </div>
                {
                  isCashless ?
                  <AllergiesIndicator className="dinner-booking__allergies-indicator" dinnerBookingID={dinnerBookingID} /> :
                  null
                }
                {
                  hasFSMEligibility && hasFSMLimit && isCashless ?
                  <div className="dinner-booking__fsm-limit">
                    Free Meals Limit: <Currency className="dinner-booking__fsm-limit-amount" amount={fsmLimitPerDay} />/Day
                  </div> :
                  null
                }
                <div className="dinner-booking__row">
                  <div className="dinner-booking__total">
                    Total: <Currency className="dinner-booking__total-amount" amount={totalPrice} />
                  </div>
                  <AddButton className="dinner-booking__add-button" onClick={this.confirmAddToBasket} />
                </div>
                {
                  hasLastModifiedDate ?
                  <div className="dinner-booking__last-modified">
                    <span className="dinner-booking__last-modified-label">Last Modified </span>
                    <span className="dinner-booking__last-modified-date">
                      <ShortDate date={lastModifiedDate} />
                    </span>
                  </div> :
                  null
                }
              </div>
            </ExpandingBox.Body>
          </ExpandingBox>
          <AllergiesModal
            dinnerBookingID={dinnerBookingID}
            isWarning={true}
            isOpen={this.state.isAllergiesWarningModalOpen}
            onClose={this.onAllergiesWarningModalClose}
          />
        </Highlight>
      </div>
    )

  }

}

export default connect(
  (state, { dinnerBookingID }) => ({
    dinnerBooking: getDinnerBookingByID(state, dinnerBookingID),
    totalPrice: getDinnerBookingTotalPrice(state, dinnerBookingID),
    allergyInformation: getDinnerBookingAllergyInformation(state, dinnerBookingID),
    isAllergiesWarningEnabled: isAllergiesWarningEnabled(state),
    isExpanded: isDinnerBookingExpanded(state, dinnerBookingID),
    hasFSMEligibility: doesDinnerBookingHaveFSMEligibility(state, dinnerBookingID),
    isInBasket: isDinnerBookingInBasket(state, dinnerBookingID),
    isModified: hasDinnerBookingBeenModified(state, dinnerBookingID),
    isSeen: isDinnerBookingSeen(state, dinnerBookingID),
    isBooked: doesDinnerBookingHaveBookings(state, dinnerBookingID),
    isHighlighted: dinnerBookingID === getGoToDinnerBookingID(state)
  }),
  (dispatch, { dinnerBookingID }) => bindActionCreators({
    addToBasket: () => addDinnerBookingToBasket(dinnerBookingID),
    removeFromBasket: () => removeDinnerBookingFromBasket(dinnerBookingID),
    expand: () => setExpandedDinnerBooking(dinnerBookingID),
    collapse: () => setExpandedDinnerBooking(null)
  }, dispatch)
)(DinnerBooking);



// WEBPACK FOOTER //
// ./src/components/school_money/dinners/DinnerBooking.js